"use client"

import { useCallback, useEffect, useState } from "react"

import { db } from "@/app/pages/offlinePage/db/db"
import { db as favoritesDb } from "@/app/pages/favoritosPage/db/db"

type Song = {
  index: string
  songName: string
  songArtist: string
  songimg: string
  link: string
  trackTime: string
}

type UsePlaylistTrackStateProps = {
  song: Song
}

export default function usePlaylistTrackState({
  song,
}: UsePlaylistTrackStateProps) {
  const [isOfflineSaved, setIsOfflineSaved] =
    useState(false)

  const [isFavorite, setIsFavorite] =
    useState(false)

  const [isSavingOffline, setIsSavingOffline] =
    useState(false)

  /*
   * ============================================================
   * INITIAL DATABASE STATE
   * ============================================================
   */

  useEffect(() => {
    let cancelled = false

    const loadState = async () => {
      try {
        const [offlineSong, favorite] =
          await Promise.all([
            db.songs.get(song.link),
            favoritesDb.favorites.get(song.link),
          ])

        if (cancelled) return

        setIsOfflineSaved(
          !!offlineSong &&
          offlineSong.offline === "si"
        )

        setIsFavorite(!!favorite)
      } catch (error) {
        console.error(
          "Error comprobando estado de la canción:",
          error
        )
      }
    }

    loadState()

    return () => {
      cancelled = true
    }
  }, [song.link])

  /*
   * ============================================================
   * FAVORITE
   * ============================================================
   */

  const toggleFavorite = useCallback(async () => {
    try {
      const existing =
        await favoritesDb.favorites.get(song.link)

      if (existing) {
        await favoritesDb.favorites.delete(
          song.link
        )

        setIsFavorite(false)
        return false
      }

      await favoritesDb.favorites.put({
        url: song.link,
        name: song.songName,
        artist: song.songArtist,
        songimg: song.songimg,
        trackTime: song.trackTime,
        savedAt: new Date(),
      })

      setIsFavorite(true)

      return true
    } catch (error) {
      console.error(
        "Error gestionando favorito:",
        error
      )

      return isFavorite
    }
  }, [
    song.link,
    song.songName,
    song.songArtist,
    song.songimg,
    song.trackTime,
    isFavorite,
  ])

  /*
   * ============================================================
   * SAVE OFFLINE
   * ============================================================
   */

  const saveOffline = useCallback(async () => {
    if (
      isOfflineSaved ||
      isSavingOffline
    ) {
      return false
    }

    setIsSavingOffline(true)

    try {
      /*
       * IMPORTANTE:
       *
       * No tocamos Redux.
       * No tocamos el reproductor.
       * No cambiamos isPlaying.
       */

      const response =
        await fetch(song.link)

      if (!response.ok) {
        throw new Error(
          `Error HTTP ${response.status}`
        )
      }

      const blob =
        await response.blob()

      await db.songs.put({
        url: song.link,
        name: song.songName,
        artist: song.songArtist,
        songimg: song.songimg,
        blob,
        offline: "si",
        savedAt: new Date(),
      })

      setIsOfflineSaved(true)

      return true
    } catch (error) {
      console.error(
        "Error guardando canción offline:",
        error
      )

      return false
    } finally {
      setIsSavingOffline(false)
    }
  }, [
    isOfflineSaved,
    isSavingOffline,
    song.link,
    song.songName,
    song.songArtist,
    song.songimg,
  ])

  return {
    isOfflineSaved,
    isFavorite,
    isSavingOffline,
    toggleFavorite,
    saveOffline,
  }
}